import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  getSubjectBackgroundClass,
  getSubjectTextClass,
} from '@helpers/subject'
import {
  AVATAR_SIZE,
  IMAGE_SIZES,
  HEADING_TYPE,
  BUTTON_TYPE,
  COLORS,
  TEXT_SIZES,
  BUTTON_SIZE,
  USER_ROLES,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import { storagePath } from '@helpers/variables'
import Heading from '@components/Heading'
import Avatar from '@components/Avatar'
import Image from '@components/Image'
import Button from '@components/Button'
import Text from '@components/Text'
import { StudentBookCardStyle } from './style'
import { ImageSize } from '@components/Image/style'
import Icon from '@components/Icon'
import { getBookImageUrl } from '@helpers/book'
import classNames from 'classnames'
import { useCurrentUser } from '@hooks/useCurrentUser'
import Tooltip from '@components/Tooltip'
import withTooltip from '@components/WithTooltip'

const ButtonWithTooltip = withTooltip(Button)

const StudentBookCard = (props) => {
  const { unShare, book, resourceType } = props
  const { id, subject, schoolGroups } = book
  const { currentUser } = useCurrentUser()
  const userRole = currentUser?.userRole
  const [groupsButtonRef, setGroupsButtonRef] = useState()
  const [showGroupsTooltip, setShowGroupsTooltip] = useState()
  const schoolGroup = schoolGroups?.length ? schoolGroups[0] : undefined
  const placeholder = '/static/teacher_placeholder.jpg'
  const bgClass = subject && getSubjectBackgroundClass(subject.slug)
  const shareButtonRef = useRef()
  const [showShareTooltip, setShowShareTooltip] = useState(false)

  const sharer = () => {
    if (
      resourceType === SCHOOL_RESOURCE_TYPES.DEMO_CLASS ||
      resourceType === SCHOOL_RESOURCE_TYPES.DEMO_GROUP
    ) {
      return currentUser
    } else {
      return book?.pivot?.sharedBy
    }
  }

  // sharedBy is null when the teacher who shared the book has been deleted
  const sharedBy = sharer()
  const teacherName = sharedBy
    ? `${sharedBy.first_name} ${sharedBy.last_name}`
    : 'Borttagen lärare'

  return (
    <div
      className={`p-4 ${bgClass} flex flex-col items-center rounded-[4px] bg-opacity-25`}
    >
      <div className={'mb-4 flex justify-between w-full'}>
        {!schoolGroups?.length && <div className={'flex items-center'} />}
        {schoolGroups?.length === 1 && (
          <div
            className={
              'relative bg-candyRed self-center py-2 px-4 left-[-24px] mr-[-24px]'
            }
          >
            <p className='text-white text-xxSmall w-min max-w-[5rem] text-ellipsis overflow-hidden '>
              {schoolGroups?.[0]?.name}
            </p>
          </div>
        )}
        {schoolGroups?.length > 1 && (
          <>
            <div
              className={'self-center'}
              onMouseOver={() => setShowGroupsTooltip(true)}
              onMouseLeave={() => setShowGroupsTooltip(false)}
            >
              <Button
                ref={setGroupsButtonRef}
                backgroundColor={COLORS.CANDY_RED.BACKGROUND}
                textColor={COLORS.WHITE.TEXT}
                size={BUTTON_SIZE.MEDIUM}
                active={showGroupsTooltip}
                classes={'self-center'}
              >
                {schoolGroups.length} grupper
              </Button>
            </div>
            <Tooltip
              show={showGroupsTooltip}
              referenceElement={groupsButtonRef}
              onHide={() => setShowGroupsTooltip(false)}
            >
              {schoolGroups.map((sg) => (
                <p key={sg.id} className={COLORS.WHITE.TEXT}>
                  {sg.name}
                </p>
              ))}
            </Tooltip>
          </>
        )}
        <div
          className={'flex items-center justify-end'}
          style={schoolGroup && { width: 'calc(100% - 5.5rem)' }}
        >
          {/*
            TODO: Is this check below needed?
            No resourceType is passed to component when a student is viewing it in StudentBookList.
          */}

          {/* {(resourceType === SCHOOL_RESOURCE_TYPES.CLASS ||
            resourceType === SCHOOL_RESOURCE_TYPES.GROUP) && ( */}
          <>
            <h5
              className='pr-3 font-medium font-secondary text-black text-right text-ellipsis overflow-hidden'
              style={{ maxWidth: 'calc(100% - 3rem)' }}
            >
              {teacherName}
            </h5>
            <Avatar
              user={teacherName}
              url={`${storagePath}/${
                sharedBy?.image ? sharedBy.image : placeholder
              }`}
              size={AVATAR_SIZE.SMALL}
            />
          </>
          {/* )} */}
        </div>
      </div>

      <div className={'w-[200px] grow'}>
        <Card book={book} />
      </div>

      <div
        className={classNames('mt-6 flex justify-center', {
          'self-start': userRole !== USER_ROLES.STUDENT,
        })}
      >
        <Button
          size={BUTTON_SIZE.MEDIUM}
          type={BUTTON_TYPE.PRIMARY}
          to={`/bok/${id}`}
        >
          Öppna boken
        </Button>

        {userRole !== USER_ROLES.STUDENT && (
          <div
            ref={shareButtonRef}
            onMouseOver={() => setShowShareTooltip(true)}
            onMouseLeave={() => setShowShareTooltip(false)}
          >
            <ButtonWithTooltip
              type={BUTTON_TYPE.DARK_GREY}
              size={BUTTON_SIZE.ICON_CIRCLE}
              margin
              handleClick={() => {
                unShare()
              }}
              icon={'Exit'}
              disabled={!!book.schoolGroups}
              tooltip={
                book.schoolGroups
                  ? undefined
                  : `Sluta dela boken med denna ${
                      resourceType === SCHOOL_RESOURCE_TYPES.CLASS
                        ? 'klass'
                        : 'grupp'
                    }.`
              }
            />
            {book.schoolGroups && (
              <Tooltip
                referenceElement={shareButtonRef.current}
                show={showShareTooltip}
              >
                <Text color={COLORS.WHITE.TEXT} noPadding>
                  Du kan bara sluta dela den här boken via gruppen.
                </Text>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

StudentBookCard.propTypes = {
  book: PropTypes.object,
  resourceType: PropTypes.string,
}

const Card = (props) => {
  const { book } = props
  const {
    name,
    imageAlt,
    subject,
  } = book
  const subjectName = subject?.name
  const imageUrl = getBookImageUrl(book)
  const subjectColor = getSubjectTextClass(subject?.slug)

  return (
    <div className={StudentBookCardStyle.cardContainer}>
      <div className={StudentBookCardStyle.imageWrapper}>
        {imageUrl && (
          <Image
            size={IMAGE_SIZES.MEDIUM}
            imageUrl={imageUrl}
            altText={imageAlt}
          />
        )}
        {!imageUrl && <div className={ImageSize.medium} />}
      </div>

      <div className={StudentBookCardStyle.text}>
        <div className={`${subjectColor} flex`}>
          <Icon icon={'Book'} />
          <Text
            size={TEXT_SIZES.XXXSMALL}
            color={subjectColor}
            extraClasses={'pl-2'}
          >
            {subjectName}
          </Text>
        </div>
        <div>
          <Heading
            size={TEXT_SIZES.BASE}
            headingLevel={HEADING_TYPE.H2}
            color={COLORS.WHITE.TEXT}
          >
            {name}
          </Heading>
        </div>
      </div>
    </div>
  )
}

export default StudentBookCard
